import React, { useEffect, useState } from "react";
import {
  listLeagueRoles,
  setLeagueRole,
  clearLeagueRole,
} from "../lib/api";

/**
 * ManageStaffPanel — drops into LeagueEditModal as the STAFF tab.
 *
 * Owner-only by enforcement (the API gates every mutation). This UI
 * doesn't grant the owner role — that flows through the ownership-
 * transfer endpoint, not here.
 *
 * Roles surfaced:
 *   • OWNER     — read-only chip
 *   • CO-ADMIN  — broad management. Granted via the dropdown.
 *   • MODERATOR — chat/dispute/scoring focus. Granted via the dropdown.
 *
 * Grant flow:
 *   1. Owner types a user_id (we don't have a "search users" endpoint
 *      yet — Phase B can add typeahead later).
 *   2. Owner picks the role.
 *   3. POST /leagues/{id}/roles/{user_id} {role}.
 *   4. List refreshes; legacy `co_admins` array mirrors automatically.
 */
export function ManageStaffPanel({ league, onToast }) {
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState("co_admin");

  const load = async () => {
    if (!league?.league_id) return;
    setBusy(true);
    try {
      const res = await listLeagueRoles(league.league_id);
      setStaff(res.roles || []);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't load staff.");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [league?.league_id]);

  const grant = async () => {
    const uid = userId.trim();
    if (!uid) return onToast?.("Enter a user_id first.");
    setBusy(true);
    try {
      await setLeagueRole(league.league_id, uid, role);
      onToast?.(`✅ ${
        role === "co_admin" ? "Co-admin" :
        role === "moderator" ? "Moderator" :
        "Caster"
      } granted.`);
      setUserId("");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Grant failed.");
    } finally { setBusy(false); }
  };

  const revoke = async (uid, name) => {
    if (!window.confirm(`Revoke role from ${name || uid}?`)) return;
    setBusy(true);
    try {
      await clearLeagueRole(league.league_id, uid);
      onToast?.("🛑 Role revoked.");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Revoke failed.");
    } finally { setBusy(false); }
  };

  const rolePillColor = (r) =>
    r === "owner"
      ? "bg-yellow-400/15 text-yellow-300 border-yellow-400/40"
      : r === "co_admin"
        ? "bg-[#14b8a6]/15 text-[#14b8a6] border-[#14b8a6]/45"
        : r === "caster"
          ? "bg-red-400/15 text-red-300 border-red-400/40"
          : "bg-fuchsia-400/15 text-fuchsia-300 border-fuchsia-400/40";

  return (
    <div data-testid="manage-staff-panel" className="space-y-4">
      <div className="glass rounded-2xl p-4 border border-[#14b8a6]/25">
        <p className="text-[10px] tracking-[3px] font-bold text-[#14b8a6] mb-2">
          <i className="fas fa-user-shield mr-1.5" />GRANT ROLE
        </p>
        <p className="text-[11px] text-white/55 mb-3 leading-snug">
          Co-admins manage settings, matches and broadcasts.
          Moderators handle chat, disputes and result corrections.
          Casters post stream URLs + clips for matches.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="user_id (e.g. user_a1b2c3d4)"
            data-testid="staff-grant-user-id"
            className="flex-1 bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm font-mono"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            data-testid="staff-grant-role-select"
            className="bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
          >
            <option value="co_admin">Co-Admin</option>
            <option value="moderator">Moderator</option>
            <option value="caster">Caster</option>
          </select>
          <button
            type="button"
            onClick={grant}
            disabled={busy || !userId.trim()}
            data-testid="staff-grant-submit"
            className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-neon disabled:opacity-40"
          >
            <i className="fas fa-plus mr-1" />GRANT
          </button>
        </div>
      </div>

      <div data-testid="staff-list" className="space-y-2">
        <p className="text-[10px] tracking-[3px] font-bold text-white/55">
          CURRENT STAFF ({staff.length})
        </p>
        {staff.length === 0 ? (
          <p className="text-[12px] text-white/40 text-center py-4">
            {busy ? "Loading…" : "No staff yet — grant a role above."}
          </p>
        ) : (
          staff.map((s) => (
            <div
              key={s.user_id}
              data-testid={`staff-row-${s.user_id}`}
              className="glass rounded-xl p-3 flex items-center gap-3 border border-white/8"
            >
              <div className="w-9 h-9 rounded-xl bg-black/40 border border-white/15 flex items-center justify-center overflow-hidden shrink-0">
                {s.pfp_url
                  ? <img alt="" src={s.pfp_url} className="w-full h-full object-cover" />
                  : <i className="fas fa-user text-white/50" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{s.name}</p>
                <p className="text-[10px] text-white/40 font-mono truncate">{s.user_id}</p>
              </div>
              <span
                className={`text-[9px] tracking-widest px-2 py-1 rounded-full border ${rolePillColor(s.role)}`}
              >
                {s.role.replace("_", "-").toUpperCase()}
              </span>
              {s.role !== "owner" && (
                <button
                  type="button"
                  onClick={() => revoke(s.user_id, s.name)}
                  disabled={busy}
                  data-testid={`staff-revoke-${s.user_id}`}
                  className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-red-500/10 text-red-300 border border-red-500/40 hover:bg-red-500/20 disabled:opacity-40"
                >
                  <i className="fas fa-times mr-1" />REVOKE
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ManageStaffPanel;
